import type { BlockType } from '@unifyapps/defs/types/block';
import { EventTypeEnum } from '@unifyapps/defs/types/event';
import useEventCallback from '@unifyapps/hooks/useEventCallback';
import type { ActionHandlerType, OnActionArgs } from '../../../../context/ActionsProvider/context';
import useInterfaceEventBus from '../../../../hooks/useInterfaceEventBus';
import { useGlobalStateStore } from '../../../../stores/GlobalStateStore';
import { useGetGlobalStateStoreStateByInstanceId } from '../../../../stores/GlobalStateStore/hook';
import { useGetInterfaceStoreStateByInstanceId } from '../../../../stores/InterfaceStore';
import { useGetNoCodeComputedData } from '../../../computeContext/useGetNoCodeComputedData';
import { SHOULD_NOT_PROPAGATE_UP } from '../../const';

type ControlBlockSelectionPayload = {
  method: 'startBlockSelection' | 'stopBlockSelection';
};

export function useControlBlockSelection() {
  // host page GSS — for storing block selection results
  const setBlockSelection = useGlobalStateStore().hooks.useActions().setBlockSelection;
  const { getNoCodeComputedData } = useGetNoCodeComputedData();
  const getGlobalStateStoreState = useGetGlobalStateStoreStateByInstanceId();

  const controlBlockSelection: ActionHandlerType = useEventCallback(
    ({ action, actionContext, instanceId }: OnActionArgs) => {
      const payload = action.payload as ControlBlockSelectionPayload;
      const method = getNoCodeComputedData<string>(payload?.method ?? '', actionContext);
      const { actions } = getGlobalStateStoreState(instanceId);

      if (method === 'startBlockSelection') {
        return new Promise<boolean>((resolve) => {
          actions.setPreBuiltFlow({
            type: 'blockSelector',
            data: {
              onSuccess: (selectedBlocks: BlockType[], hoveredBlock: BlockType | null) => {
                setBlockSelection({ selectedBlocks, hoveredBlock });
                // dispatchPageEvent fires OnComponentSelected on the module's own page —
                // the same page where the user configures the action (e.g. show snackbar).
                // It is seeded by useInboundEventEffects via useBlockEvents(inboundEvents).
                const dispatchPageEvent = getGlobalStateStoreState(instanceId).dispatchPageEvent;
                if (!dispatchPageEvent) {
                  resolve(SHOULD_NOT_PROPAGATE_UP);
                  return;
                }

                void dispatchPageEvent({
                  eventType: EventTypeEnum.OnComponentSelected,
                  actionContext: {
                    [EventTypeEnum.OnComponentSelected]: { selectedBlocks, hoveredBlock },
                  },
                });
                resolve(SHOULD_NOT_PROPAGATE_UP);
              },

              onCancel: () => resolve(SHOULD_NOT_PROPAGATE_UP),
            },
          });
        });
      }

      if (method === 'stopBlockSelection') {
        getGlobalStateStoreState(instanceId).flows.blockSelector?.onCancel?.();
        actions.removePreBuiltFlow('blockSelector');
        return Promise.resolve(SHOULD_NOT_PROPAGATE_UP);
      }

      return Promise.resolve(SHOULD_NOT_PROPAGATE_UP);
    },
  );

  return { controlBlockSelection };
}
